/**
 * React hook wrapper for autofill that integrates with React Query
 */

import { useQueryClient } from "@tanstack/react-query";
import { autoFill, type AutoFillOptions } from "./autoFill";
import { erp } from "@/lib/erp";
import type { DocTypeSchema } from "@/lib/erp/meta";
import { getERPClient } from "@/lib/erp/client";
import { parseLinkSearchResponse } from "@/lib/erp/utils/linkUtils";

/**
 * Hook to run autofill with React Query integration
 */
export function useAutoFill() {
  const queryClient = useQueryClient();

  const runAutoFill = async (
    doctype: string,
    metaSchema: DocTypeSchema,
    getFormData: () => Record<string, any>,
    applyPatch: (patch: Record<string, any>) => void,
    applyTablePatch: (fieldname: string, rows: any[]) => void,
    options: AutoFillOptions = {}
  ) => {
    // Ensure meta function (uses React Query cache)
    const ensureMeta = async (targetDoctype: string): Promise<DocTypeSchema> => {
      // Check cache first
      const cached = queryClient.getQueryData<DocTypeSchema>(["meta", targetDoctype]);
      if (cached) {
        return cached;
      }
      
      // Fetch if not cached
      return await queryClient.ensureQueryData({
        queryKey: ["meta", targetDoctype],
        queryFn: () => erp.meta.getDocTypeSchema(targetDoctype),
      });
    };

    // Search link function (with caching)
    const searchLink = async (
      targetDoctype: string,
      filters: Record<string, any> | undefined
    ): Promise<string | null> => {
      // Create cache key
      const filterKey = filters ? JSON.stringify(filters) : "nofilters";
      const cacheKey = `link-search:${targetDoctype}:${filterKey}`;
      
      // Check cache
      const cached = sessionStorage.getItem(cacheKey);
      if (cached) {
        try {
          const results = JSON.parse(cached);
          if (Array.isArray(results) && results.length > 0) {
            const randomIndex = Math.floor(Math.random() * results.length);
            return results[randomIndex].value;
          }
        } catch {
          // Invalid cache, continue to fetch
        }
      }

      // Fetch from API
      try {
        const client = getERPClient();
        const response = await client.call("frappe.desk.search.search_link", {
          doctype: targetDoctype,
          txt: "%",
          page_length: 20,
          filters,
        });

        const results = parseLinkSearchResponse(response, { doctype: targetDoctype });
        
        // Cache results (session storage, TTL: session)
        if (results.length > 0) {
          sessionStorage.setItem(cacheKey, JSON.stringify(results));
        }

        if (results.length === 0) {
          if (import.meta.env.DEV) {
            console.warn(`[autofill] No results for ${targetDoctype} link field`);
          }
          return null;
        }

        const randomIndex = Math.floor(Math.random() * results.length);
        return results[randomIndex].value;
      } catch (error) {
        if (import.meta.env.DEV) {
          console.warn(`[autofill] Link search failed for ${targetDoctype}:`, error);
        }
        return null;
      }
    };

    // Run autofill
    const result = await autoFill(
      doctype,
      metaSchema,
      getFormData,
      ensureMeta,
      searchLink,
      options
    );

    // Apply patches
    if (Object.keys(result.fieldPatch).length > 0) {
      applyPatch(result.fieldPatch);
    }

    for (const tablePatch of result.tablePatches) {
      applyTablePatch(tablePatch.fieldname, tablePatch.rows);
    }
  };

  return { runAutoFill };
}
